// AI assisted development
/**
 * Header notification center for feedback and pipeline status alerts.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  Typography,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  useClearNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../../hooks/useNotifications';
import { usePipelineStatusNotifications } from '../../hooks/usePipelineStatusNotifications';
import type { DashboardNotification } from '../../types/notification';

function relativeTime(value: string): string {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '';
  const seconds = Math.max(1, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function sortNotifications(items: DashboardNotification[]): DashboardNotification[] {
  return [...items].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  );
}

interface NotificationCenterProps {
  enablePipelineNotifications?: boolean;
}

export default function NotificationCenter({
  enablePipelineNotifications = true,
}: NotificationCenterProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const serverNotifications = useNotifications();
  const markServerRead = useMarkNotificationRead();
  const markAllServerRead = useMarkAllNotificationsRead();
  const clearServerNotifications = useClearNotifications();
  const pipelineNotifications = usePipelineStatusNotifications(enablePipelineNotifications);
  const open = Boolean(anchorEl);

  const items = useMemo(
    () =>
      sortNotifications([
        ...(serverNotifications.data?.items ?? []),
        ...pipelineNotifications.items,
      ]).slice(0, 50),
    [serverNotifications.data?.items, pipelineNotifications.items]
  );
  const unreadCount = (serverNotifications.data?.unreadCount ?? 0) + pipelineNotifications.unreadCount;

  const handleItemClick = (item: DashboardNotification) => {
    if (item.isLocal) {
      pipelineNotifications.markRead(item.id);
    } else if (typeof item.id === 'number' && !item.readAt) {
      markServerRead.mutate(item.id);
    }
    setAnchorEl(null);
    if (item.linkUrl) {
      navigate(item.linkUrl);
    }
  };

  const handleMarkAllRead = () => {
    markAllServerRead.mutate();
    pipelineNotifications.markAllRead();
  };

  const handleClearAll = () => {
    clearServerNotifications.mutate();
    pipelineNotifications.clearAll();
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="Notifications"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { width: 380, maxWidth: 'calc(100vw - 32px)' } }}
      >
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" onClick={handleMarkAllRead} disabled={unreadCount === 0}>
              Mark all read
            </Button>
            <Button size="small" color="error" onClick={handleClearAll} disabled={items.length === 0}>
              Clear all
            </Button>
          </Box>
        </Box>
        <Divider />
        {items.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 460, overflow: 'auto' }}>
            {items.map((item) => (
              <ListItemButton
                key={`${item.isLocal ? 'local' : 'server'}-${item.id}`}
                onClick={() => handleItemClick(item)}
                sx={{
                  alignItems: 'flex-start',
                  bgcolor: item.readAt ? 'transparent' : 'action.hover',
                  borderLeft: 3,
                  borderColor: item.readAt ? 'transparent' : 'primary.main',
                }}
              >
                <ListItemText
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                      <Typography variant="body2" sx={{ fontWeight: item.readAt ? 500 : 700 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {relativeTime(item.createdAt)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary" component="span">
                        {item.body}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {item.source}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
}
